/** Show every configured role, including new roles that do not have users yet. */
export function getVisibleRoles(roles = []) {
	return roles
}

export const ROLE_LABELS = {
	SuperAdmin: 'Super Admin',
	Admin: 'Admin',
	PeeguCounselor: 'MyPeegu Experts',
	ScCounselor: 'School Counsellor',
	SSECounselor: 'School Special Educator',
	ScPrincipal: 'Senior Leadership Team',
	Teacher: 'Teacher',
}

/** App feature key for One-to-One (Individual Case) MyPeegu voice counselling session. */
export const ONE_TO_ONE_COUNSELLING_FEATURE = 'OneToOneCounsellingSessionManagement'

/** LiveKit Safe Space Survey with 18-question coverage (Expert + Super Admin). */
export const SAFE_SPACE_SURVEY_FEATURE = 'SafeSpaceSurveyManagement'

export const FEATURE_LABELS = {
	SchoolManagement: 'Schools',
	ClassroomManagement: 'Classrooms',
	StudentManagement: 'Students',
	TeacherManagement: 'Teachers',
	CounselorManagement: 'Users / Counsellors',
	IndividualCaseManagement: 'Individual Case',
	ObservationManagement: 'Observation',
	BaselineManagement: 'Baseline',
	GandTManagement: 'G&T',
	SelfAssessmentManagement: 'Self Assessment',
	SELCurriculumTrackerManagement: 'SEL Curriculum Tracker',
	SELTemplateSetupManagement: 'SEL Template Setup',
	ResourceManagement: 'Resources (RMS)',
	VirtualCounsellingManagement: 'Virtual Counselling',
	[ONE_TO_ONE_COUNSELLING_FEATURE]: 'One-to-One Counselling Session',
	[SAFE_SPACE_SURVEY_FEATURE]: 'Safe Space Survey',
	studentCheckList: 'Send Checklist',
	'student-IEP': 'Student IEP',
	BehaviourInterventionPlan: 'Behaviour Intervention Plan',
	StudentCopeManagement: 'Student COPE',
	studentWellBeing: 'Student Well-being',
	TeacherIRI: 'Teacher IRI',
	teacherProfiling: 'Teacher Profiling',
}

/**
 * Permission matrix rows aligned with dashboard main menu and sub-menu.
 * Multiple rows may share the same feature key (e.g. Observation → Safe Spaces).
 */
export const PERMISSION_MENU_ROWS = [
	{ menu: 'Users', submenu: 'Users / Counsellors', feature: 'CounselorManagement' },
	{ menu: 'Resources', submenu: 'Dashboard / All Resources / Upload', feature: 'ResourceManagement' },
	{ menu: 'Academic', submenu: 'Schools', feature: 'SchoolManagement' },
	{ menu: 'Academic', submenu: 'Classrooms', feature: 'ClassroomManagement' },
	{ menu: 'Academic', submenu: 'Students', feature: 'StudentManagement' },
	{ menu: 'Academic', submenu: 'Teachers', feature: 'TeacherManagement' },
	{ menu: 'Initiations', submenu: 'Observation', feature: 'ObservationManagement' },
	{ menu: 'Initiations', submenu: 'Observation V2', feature: 'ObservationManagement' },
	{ menu: 'Initiations', submenu: 'Individual Case', feature: 'IndividualCaseManagement' },
	{ menu: 'Initiations', submenu: 'Baseline', feature: 'BaselineManagement' },
	{ menu: 'Initiations', submenu: 'SEL Curriculum Tracker', feature: 'SELCurriculumTrackerManagement' },
	{ menu: 'Initiations', submenu: 'Send Checklist', feature: 'studentCheckList' },
	{ menu: 'Initiations', submenu: 'Student IEP', feature: 'student-IEP' },
	{ menu: 'Initiations', submenu: 'Behaviour Intervention Plan', feature: 'BehaviourInterventionPlan' },
	{ menu: 'Initiations', submenu: 'G&T', feature: 'GandTManagement' },
	{ menu: 'Initiations', submenu: 'Self Assessment Dashboard', feature: 'SelfAssessmentManagement' },
	{ menu: 'Assessment', submenu: 'Teacher Profiling', feature: 'teacherProfiling' },
	{ menu: 'Assessment', submenu: 'Teacher IRI', feature: 'TeacherIRI' },
	{ menu: 'Assessment', submenu: 'Student COPE', feature: 'StudentCopeManagement' },
	{ menu: 'Assessment', submenu: 'Student Well-being', feature: 'studentWellBeing' },
	{ menu: 'Assessment', submenu: 'Safe Spaces', feature: 'ObservationManagement' },
	{ menu: 'Assessment', submenu: 'SEL Competency', feature: 'SELCurriculumTrackerManagement' },
	{ menu: 'Assessment', submenu: 'Pulse Digital Wellbeing', feature: 'studentWellBeing' },
	{ menu: 'Assessment', submenu: 'Heart Educators', feature: 'teacherProfiling' },
	{ menu: 'Assessment', submenu: 'Student Self Assessment', feature: 'SelfAssessmentManagement' },
	{ menu: 'SEL Template Setup', submenu: 'SEL Template Admin', feature: 'SELTemplateSetupManagement' },
	{ menu: 'Virtual Counselling', submenu: 'Sessions / Schedule Meeting', feature: 'VirtualCounsellingManagement' },
	{ menu: 'Virtual Counselling', submenu: 'One-to-One Counselling Session', feature: ONE_TO_ONE_COUNSELLING_FEATURE },
	{ menu: 'Safe Space Survey', submenu: 'Sessions / History / Dashboard', feature: SAFE_SPACE_SURVEY_FEATURE },
]

/** Matrix display order — One-to-One sits under Virtual Counselling group. */
const FEATURE_MATRIX_ORDER = [
	'SchoolManagement',
	'ClassroomManagement',
	'StudentManagement',
	'TeacherManagement',
	'CounselorManagement',
	'IndividualCaseManagement',
	'ObservationManagement',
	'BaselineManagement',
	'GandTManagement',
	'SelfAssessmentManagement',
	'SELCurriculumTrackerManagement',
	'SELTemplateSetupManagement',
	'ResourceManagement',
	'VirtualCounsellingManagement',
	ONE_TO_ONE_COUNSELLING_FEATURE,
	SAFE_SPACE_SURVEY_FEATURE,
	'studentCheckList',
	'student-IEP',
	'BehaviourInterventionPlan',
	'StudentCopeManagement',
	'studentWellBeing',
	'TeacherIRI',
	'teacherProfiling',
]

export function sortAppFeaturesForMatrix(appFeatures = []) {
	const rank = (name) => {
		const idx = FEATURE_MATRIX_ORDER.indexOf(name)
		return idx === -1 ? 999 : idx
	}
	return [...appFeatures].sort((a, b) => {
		const byRank = rank(a.name) - rank(b.name)
		return byRank !== 0 ? byRank : String(a.name || '').localeCompare(String(b.name || ''))
	})
}

/**
 * Group permission rows by main menu for the matrix editor.
 *
 * Several menus share one feature key (Observation drives Observation, Observation V2
 * and Safe Spaces). Those rows cannot be granted independently, so only the first
 * occurrence is editable and the rest are flagged as mirrors of it.
 */
export function groupPermissionMenuRows(appFeatures = []) {
	const featureByName = new Map(appFeatures.map((feature) => [feature.name, feature]))
	const groups = []
	const primaryByFeature = new Map()

	for (const row of PERMISSION_MENU_ROWS) {
		const feature = featureByName.get(row.feature)
		if (!feature) continue
		let group = groups.find((entry) => entry.menu === row.menu)
		if (!group) {
			group = { menu: row.menu, rows: [] }
			groups.push(group)
		}

		const primary = primaryByFeature.get(row.feature)
		const entry = {
			...row,
			featureId: feature._id,
			featureName: feature.name,
			isPrimary: !primary,
			controlledBy: primary ? `${primary.menu} → ${primary.submenu}` : null,
			mirroredIn: [],
		}

		if (primary) {
			primary.entry.mirroredIn.push(`${row.menu} → ${row.submenu}`)
		} else {
			primaryByFeature.set(row.feature, { menu: row.menu, submenu: row.submenu, entry })
		}

		group.rows.push(entry)
	}

	return groups
}

export function getRoleLabel(roleName) {
	if (!roleName) return ''
	return ROLE_LABELS[roleName] || String(roleName).replace(/([A-Z])/g, ' $1').trim()
}

export function getFeatureLabel(featureName) {
	return FEATURE_LABELS[featureName] || featureName?.replace(/([A-Z])/g, ' $1').trim()
}

export function roleToEditorState(role, appFeatures = [], allRoles = []) {
	const featureState = {}
	appFeatures.forEach((feature) => {
		const existing = role?.appFeatures?.find(
			(f) => String(f.featureId) === String(feature._id),
		)
		featureState[feature._id] = {
			featureId: feature._id,
			featureName: feature.name,
			view: existing?.actions?.includes('view') || false,
			edit: existing?.actions?.includes('edit') || false,
			delete: existing?.actions?.includes('delete') || false,
		}
	})

	const manageableRoles = {}
	allRoles.forEach((r) => {
		manageableRoles[r.roleName] = role?.userOperationPermissions?.includes(r.roleName) || false
	})

	return { featureState, manageableRoles }
}

export function editorStateToPayload(featureState, manageableRoles) {
	return {
		appFeatures: Object.values(featureState).map((feature) => ({
			featureId: feature.featureId,
			featureName: feature.featureName,
			actions: ['view', 'edit', 'delete'].filter((action) => feature[action]),
		})),
		userOperationPermissions: Object.entries(manageableRoles)
			.filter(([, enabled]) => enabled)
			.map(([roleName]) => roleName),
	}
}

export function hasEditorChanges(originalRole, featureState, manageableRoles) {
	if (!originalRole) return false
	const payload = editorStateToPayload(featureState, manageableRoles)
	const originalManageable = [...(originalRole.userOperationPermissions || [])].sort()
	const nextManageable = [...payload.userOperationPermissions].sort()
	if (JSON.stringify(originalManageable) !== JSON.stringify(nextManageable)) return true

	return payload.appFeatures.some((feature) => {
		const original = originalRole.appFeatures?.find(
			(f) => String(f.featureId) === String(feature.featureId),
		)
		const originalActions = [...(original?.actions || [])].sort()
		const nextActions = [...feature.actions].sort()
		return JSON.stringify(originalActions) !== JSON.stringify(nextActions)
	})
}

/** True if any listed app-feature key grants edit (handles DB key naming variants). */
export function hasAppFeatureEdit(appPermissions, ...featureKeys) {
	return featureKeys.some((key) => Boolean(appPermissions?.[key]?.edit))
}

/** Counselor bulk upload routes use editStudents → StudentManagement.edit on backend. */
export function canBulkUploadTeacherProfiling(appPermissions) {
	return hasAppFeatureEdit(
		appPermissions,
		'teacherProfiling',
		'TeacherProfiling',
		'StudentManagement',
	)
}

export function canBulkUploadTeacherIRI(appPermissions) {
	return hasAppFeatureEdit(appPermissions, 'TeacherIRI', 'StudentManagement')
}

export function getTeacherProfilingBulkUploadHint({
	isCurAcademicYear,
	profilingStatus,
}) {
	if (!isCurAcademicYear) {
		return 'Bulk upload is available only for the current academic year.'
	}
	if (profilingStatus === 'In-Active') {
		return 'Schedule is In-Active. Open the school list, edit dates, and set status to Active.'
	}
	return ''
}
